import { createUserApi } from "@/api/services/admin/user";
import { TCreateUserReqDto } from "@/api/services/admin/user/dtos/create-user.dto";
import { IconOngoingMail } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { DetailItem } from "@/components/elements/DetailItem";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { MenuNavigate, TMenuContent } from "@/components/elements/MenuNavigate/MenuNavigate";
import { Select } from "@/components/elements/Select";
import { StickyFooter } from "@/components/elements/StickyFooter/StickyFooter";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { UserProductSelect } from "@/features/organization/components/UserProductSelect";
import { useDefaultProductContractIds } from "@/features/organization/hooks/useDefaultProductContractIds";
import { createOrganizationUserSchema } from "@/features/organization/schema/createOrganizationUserSchema";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { divider, text } from "@/theme/colors";
import { EContractStatus } from "@/types/enum";
import { setErrorForm, showError } from "@/utils/error";
import { yupResolver } from "@hookform/resolvers/yup";
import { Card, CardContent, CardHeader, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useController, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

type TFormValues = Required<Omit<TCreateUserReqDto, "organizationId">>;

const INCLUDE_CONTRACT_STATUSES = [EContractStatus.DURING_CONTRACT, EContractStatus.TRIAL];

type TOrganizationUserCreateFormProps = {
  defaultProductContractIds: number[];
};

const OrganizationUserCreateForm = ({ defaultProductContractIds }: TOrganizationUserCreateFormProps) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const methods = useForm<TFormValues>({
    defaultValues: {
      fullName: "",
      email: "",
      role: "",
      productContractIds: defaultProductContractIds,
    },
    resolver: yupResolver(createOrganizationUserSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { control } = methods;

  const {
    field: { value: productContractIds, onChange: onProductContractIdsChange },
  } = useController({ control, name: "productContractIds" });

  const onSubmit = async (data: TFormValues) => {
    if (!id) return;
    try {
      await createUserApi({ ...data, organizationId: Number(id) });
      toast.success(API_SUCCESS_MESSAGE);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION_USER.FETCH_LIST] });
      navigate(`${AppRoutes.organizationDetail.replace(":id", id)}?tab=users`);
    } catch (error) {
      setErrorForm(methods, error);
      showError(error);
    }
  };

  const handleBack = () => {
    navigate(`${AppRoutes.organizationDetail.replace(":id", id!)}?tab=users`);
  };

  const contents: TMenuContent[] = useMemo(
    () => [
      {
        title: "ユーザーの状態",
        content: (
          <Card variant="outlined" sx={{ borderColor: divider.middle, borderRadius: "8px" }}>
            <CardHeader
              title={
                <Typography variant="head20Bold" color={text.primary}>
                  ユーザーの状態
                </Typography>
              }
              sx={{ pt: "24px", pb: 0, px: 3 }}
            />
            <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
              <Grid container spacing={3}>
                <Grid item xs={4.8}>
                  <DetailItem required label="ステータス" value="使用中" />
                </Grid>
                <Grid item xs={4.8}>
                  <DetailItem label="認証ステータス" value="登録メール送信前" />
                </Grid>
                <Grid
                  item
                  xs={2.4}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Button size="sm" variant="ghost" disabled>
                    パスワードリセット
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ),
      },

      {
        title: "基本情報",
        content: (
          <Card variant="outlined" sx={{ borderColor: divider.middle, borderRadius: "8px" }}>
            <CardHeader
              title={
                <Typography variant="head20Bold" color={text.primary}>
                  基本情報
                </Typography>
              }
              sx={{ pt: "24px", pb: 0, px: 3 }}
            />
            <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <FormField control={control} name="fullName">
                    <Input />
                  </FormField>
                </Grid>
                <Grid item xs={6}>
                  <FormField control={control} name="role">
                    <Select enumName="EUserRole" />
                  </FormField>
                </Grid>
                <Grid item xs={6}>
                  <FormField control={control} name="email">
                    <Input />
                  </FormField>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ),
      },
      {
        title: "プロダクトアクセス権限",
        content: (
          <UserProductSelect
            userProductContracts={[]}
            selectedContractIds={productContractIds ?? []}
            onSelectedContractIdsChange={onProductContractIdsChange}
            includeContractStatuses={INCLUDE_CONTRACT_STATUSES}
          />
        ),
      },
    ],
    [control, productContractIds, onProductContractIdsChange],
  );

  return (
    <Form height="100%" methods={methods} onSubmit={onSubmit} schema={createOrganizationUserSchema}>
      <Stack gap={3}>
        <Stack gap={1}>
          <Typography variant="head20Bold" color={text.primary}>
            新規顧客ユーザーの追加
          </Typography>
          <Typography variant="body14" color={text.secondary}>
            顧客側画面にログインできるユーザーを新しく作成し、パスワードの新規登録リンクを含むログイン情報をメールアドレス宛に送付します。
          </Typography>
        </Stack>

        <MenuNavigate
          contents={contents}
          contentAffix={
            <StickyFooter justifyContent="space-between">
              <Button size="sm" variant="ghost" onClick={handleBack}>
                戻る
              </Button>
              <Button size="sm" type="submit" sx={{ gap: 1 }}>
                <IconOngoingMail color="currentColor" width={20} height={20} />
                メールを送信
              </Button>
            </StickyFooter>
          }
        />
      </Stack>
    </Form>
  );
};

const OrganizationUserCreatePage = () => {
  const { id } = useParams();
  // Resolve the default "select all active products" here so the form mounts with the selection already
  // applied.
  const { defaultContractIds, isLoading } = useDefaultProductContractIds(id, INCLUDE_CONTRACT_STATUSES);

  if (isLoading) return <LoadingPage />;

  return <OrganizationUserCreateForm defaultProductContractIds={defaultContractIds} />;
};

export default OrganizationUserCreatePage;
